<template>
  <div class="crm-leads">
    <div class="crm-leads__totals">
      <div v-for="item in totalItems" :key="item.key" class="crm-leads__total">
        <div class="crm-leads__total-value">{{ totals[item.key] || 0 }}</div>
        <div class="crm-leads__total-label">{{ item.label }}</div>
      </div>
    </div>

    <div class="crm__filters">
      <v-text-field v-model="search" label="Пошук: ім'я, телефон, email" prepend-inner-icon="mdi-magnify" outlined dense clearable hide-details @input="reloadLater"></v-text-field>
      <v-select v-model="status" :items="statusItems" label="Статус" outlined dense clearable hide-details @change="reload"></v-select>
      <v-select v-model="source" :items="sourceItems" label="Джерело" outlined dense clearable hide-details @change="reload"></v-select>
      <CrmDateField v-model="dateFrom" label="Від" @input="reload" />
      <CrmDateField v-model="dateTo" label="До" @input="reload" />
      <v-btn color="primary" depressed :loading="exporting" @click="downloadExcel">Excel</v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100], itemsPerPageText: 'Рядків на сторінці' }"
      loading-text="Завантаження…"
      no-data-text="Заявок немає"
      class="crm__table"
    >
      <template v-slot:[`item.created_at_human`]="{ item }">
        <div>{{ item.created_at_human }}</div>
        <div v-if="item.reminded" class="caption orange--text">нагадано</div>
      </template>
      <template v-slot:[`item.psychologist`]="{ item }">
        <a v-if="item.public_url" :href="item.public_url" target="_blank" rel="noopener">{{ item.psychologist }}</a>
        <span v-else>{{ item.psychologist }}</span>
        <div class="caption grey--text">{{ item.notified_via }}</div>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <div>{{ item.name }}</div>
        <div class="caption">{{ item.phone }}<span v-if="item.email">, {{ item.email }}</span></div>
      </template>
      <template v-slot:[`item.topic`]="{ item }">
        <div class="crm__source">{{ item.topic || "—" }}</div>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-select
          :value="item.status"
          :items="statusItems"
          dense
          hide-details
          class="crm-leads__status"
          :menu-props="{ offsetY: true }"
          @change="setStatus(item, $event)"
        ></v-select>
        <div class="caption grey--text">{{ item.status_at_human }}</div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn icon small title="Видалити дані клієнта на вимогу" @click="askDelete(item)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="deleteDialog" max-width="460">
      <v-card v-if="toDelete">
        <v-card-title>Видалити дані клієнта</v-card-title>
        <v-card-text>
          <p>{{ toDelete.name }}, {{ toDelete.phone }}<span v-if="toDelete.email">, {{ toDelete.email }}</span>. Дані видаляються назавжди.</p>
          <v-checkbox v-model="deleteAll" hide-details label="Видалити всі заявки з цим телефоном або email"></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Скасувати</v-btn>
          <v-btn color="error" depressed :loading="deleting" @click="confirmDelete">Видалити</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiClient from "@/libs/http-client";
import CrmDateField from "./CrmDateField";
import { download } from "./download";

export default {
  name: "CrmLeads",
  components: { CrmDateField },
  data() {
    return {
      search: "",
      status: null,
      source: null,
      dateFrom: null,
      dateTo: null,
      rows: [],
      total: 0,
      totals: {},
      options: { page: 1, itemsPerPage: 50 },
      loading: false,
      exporting: false,
      timer: null,
      deleteDialog: false,
      toDelete: null,
      deleteAll: false,
      deleting: false,
      totalItems: [
        { key: "total", label: "заявок" },
        { key: "answered", label: "з відповіддю" },
        { key: "contacted", label: "зв'язались" },
        { key: "scheduled", label: "консультацій" },
        { key: "unreachable", label: "не вдалося" },
      ],
      statusItems: [
        { value: "new", text: "Нова (без відповіді)" },
        { value: "contacted", text: "Зв'язався" },
        { value: "scheduled", text: "Консультацію призначено" },
        { value: "unreachable", text: "Не вдалося зв'язатися" },
      ],
      sourceItems: [
        { value: "google", text: "Google" },
        { value: "instagram", text: "Instagram" },
        { value: "facebook", text: "Facebook" },
        { value: "ads", text: "Реклама" },
        { value: "other_site", text: "Інший сайт" },
        { value: "direct", text: "Прямий захід" },
      ],
      headers: [
        { text: "Дата", value: "created_at_human", sortable: false },
        { text: "Психолог", value: "psychologist", sortable: false },
        { text: "Клієнт", value: "name", sortable: false },
        { text: "Запит", value: "topic", sortable: false },
        { text: "Джерело", value: "source", sortable: false },
        { text: "Статус", value: "status", sortable: false, width: 230 },
        { text: "", value: "actions", sortable: false, width: 48 },
      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
  },
  methods: {
    params() {
      const params = {
        search: this.search || "",
        status: this.status || "",
        source: this.source || "",
        date_from: this.dateFrom || "",
        date_to: this.dateTo || "",
      };
      Object.keys(params).forEach((key) => params[key] === "" && delete params[key]);
      return params;
    },
    load() {
      this.loading = true;
      return ApiClient.get("api/admin/crm/leads", { params: { ...this.params(), page: this.options.page, per_page: this.options.itemsPerPage } })
        .then((response) => {
          if (!response) return;
          this.rows = response.data.data;
          this.total = response.data.meta.total;
          this.totals = response.data.meta.totals;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    reloadLater() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.reload, 400);
    },
    setStatus(item, status) {
      ApiClient.post(`api/admin/crm/leads/${item.id}/status`, { status }).then((response) => {
        if (response) Object.assign(item, response.data.data);
      });
    },
    askDelete(item) {
      this.toDelete = item;
      this.deleteAll = false;
      this.deleteDialog = true;
    },
    confirmDelete() {
      this.deleting = true;
      ApiClient.delete(`api/admin/crm/leads/${this.toDelete.id}?all=${this.deleteAll ? 1 : 0}`)
        .then((response) => {
          if (!response) return;
          this.deleteDialog = false;
          this.load();
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    downloadExcel() {
      this.exporting = true;
      download("api/admin/crm/leads/export", this.params(), "crm-leads").finally(() => {
        this.exporting = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.crm-leads__totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
}
.crm-leads__total {
  padding: 12px;
  border: 1px solid #00A3B7;
  border-radius: 6px;
  text-align: center;
}
.crm-leads__total-value {
  color: #00A3B7;
  font-size: 28px;
  font-weight: 500;
  line-height: 1.1;
}
.crm-leads__total-label {
  color: #575756;
  font-size: 14px;
}
.crm-leads__status {
  min-width: 200px;
  padding-top: 0;
  margin-top: 0;
}
</style>
